import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, FileText, Eye, EyeOff, Paperclip, Square, Download, Upload } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Separator } from '../ui/separator';
import TipTapEditor from '../ui/TipTapEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Predefined background colors for the textbaustein card
const BACKGROUND_COLORS = [
  { name: 'Keine', value: null },
  { name: 'Gelb', value: '#fef3c7' },
  { name: 'Grün', value: '#dcfce7' },
  { name: 'Blau', value: '#dbeafe' },
  { name: 'Rosa', value: '#fce7f3' },
  { name: 'Orange', value: '#fed7aa' },
  { name: 'Grau', value: '#f3f4f6' },
];

const TextbausteineTab = ({ onStatsUpdate }) => {
  const [textbausteine, setTextbausteine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBaustein, setEditingBaustein] = useState(null);
  const [formData, setFormData] = useState({ 
    kennung: '', 
    titel: '', 
    emoji: '',
    show_title: true, 
    inhalt: '',
    has_border: false,
    has_line_above: false,
    has_line_below: false,
    background_color: null
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [removeFile, setRemoveFile] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState('');
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const importFileRef = useRef(null);
  
  const getAvailableLetters = (currentKennung = null) => {
    const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    const usedLetters = textbausteine.map(tb => tb.kennung);
    // When editing, include the current kennung as available
    return allLetters.filter(letter => !usedLetters.includes(letter) || letter === currentKennung);
  };

  // Export function
  const handleExport = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/textbausteine/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create and download JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `textbausteine_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`${response.data.textbausteine.length} Textbausteine exportiert`);
    } catch (error) {
      toast.error('Fehler beim Exportieren');
    }
  };

  // Import function
  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/textbausteine/import`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${response.data.imported} importiert, ${response.data.skipped} übersprungen`);
      if (response.data.errors?.length > 0) {
        console.error('Import errors:', response.data.errors);
      }
      
      fetchTextbausteine();
      onStatsUpdate && onStatsUpdate();
    } catch (error) {
      toast.error('Fehler beim Importieren - ungültiges Dateiformat');
    }
    
    // Reset file input
    if (importFileRef.current) {
      importFileRef.current.value = '';
    }
  };

  useEffect(() => {
    fetchTextbausteine();
  }, []);

  const fetchTextbausteine = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/textbausteine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTextbausteine(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Textbausteine');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingBaustein(null);
    const available = getAvailableLetters();
    const firstAvailable = available.length > 0 ? available[0] : '';
    setFormData({ 
      kennung: firstAvailable, 
      titel: '', 
      emoji: '',
      show_title: true, 
      show_in_pruefung: false,
      inhalt: '',
      has_border: false,
      has_line_above: false,
      has_line_below: false,
      background_color: null
    });
    setSelectedFile(null);
    setRemoveFile(false);
    setShowEmojiPicker(false);
    setDialogOpen(true);
  };

  const handleEdit = (baustein) => {
    setEditingBaustein(baustein);
    setFormData({ 
      kennung: baustein.kennung, 
      titel: baustein.titel || '', 
      emoji: baustein.emoji || '',
      show_title: baustein.show_title !== undefined ? baustein.show_title : true,
      show_in_pruefung: baustein.show_in_pruefung || false,
      inhalt: baustein.inhalt || '',
      has_border: baustein.has_border || false,
      has_line_above: baustein.has_line_above || false,
      has_line_below: baustein.has_line_below || false,
      background_color: baustein.background_color || null
    });
    setSelectedFile(null);
    setRemoveFile(false);
    setShowEmojiPicker(false);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('admin_token');
      const formDataToSend = new FormData();
      formDataToSend.append('kennung', formData.kennung);
      formDataToSend.append('titel', formData.titel || '');
      formDataToSend.append('emoji', formData.emoji || '');
      formDataToSend.append('show_title', formData.show_title ? 'true' : 'false');
      formDataToSend.append('show_in_pruefung', formData.show_in_pruefung ? 'true' : 'false');
      formDataToSend.append('inhalt', formData.inhalt);
      formDataToSend.append('code', '');
      formDataToSend.append('has_border', formData.has_border ? 'true' : 'false');
      formDataToSend.append('has_line_above', formData.has_line_above ? 'true' : 'false');
      formDataToSend.append('has_line_below', formData.has_line_below ? 'true' : 'false');
      formDataToSend.append('background_color', formData.background_color || '');
      
      // If kennung changed, send new_kennung
      if (editingBaustein && formData.kennung !== editingBaustein.kennung) {
        formDataToSend.append('new_kennung', formData.kennung);
      }
      
      if (selectedFile) {
        formDataToSend.append('file', selectedFile);
      }
      
      if (editingBaustein && removeFile) {
        formDataToSend.append('remove_file', 'true');
      }

      if (editingBaustein) {
        await axios.put(
          `${API}/admin/textbausteine/${editingBaustein.kennung}`,
          formDataToSend,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            } 
          }
        );
        toast.success(formData.kennung !== editingBaustein.kennung 
          ? `Textbaustein zu ${formData.kennung} geändert` 
          : 'Textbaustein aktualisiert');
      } else {
        await axios.post(
          `${API}/admin/textbausteine`,
          formDataToSend,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            } 
          }
        );
        toast.success('Textbaustein erstellt');
      }

      setDialogOpen(false);
      setSelectedFile(null);
      setRemoveFile(false);
      fetchTextbausteine();
      onStatsUpdate && onStatsUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/textbausteine/${deleteTarget}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Textbaustein gelöscht');
      setDeleteTarget(null);
      fetchTextbausteine();
      onStatsUpdate && onStatsUpdate();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleDeleteAll = async () => {
    if (deleteAllConfirm !== 'LÖSCHEN') {
      toast.error('Bitte "LÖSCHEN" eingeben zur Bestätigung');
      return;
    }
    try {
      const token = localStorage.getItem('admin_token');
      const result = await axios.delete(`${API}/admin/textbausteine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${result.data.deleted_count} Textbausteine gelöscht`);
      setShowDeleteAllDialog(false);
      setDeleteAllConfirm('');
      fetchTextbausteine();
      onStatsUpdate && onStatsUpdate();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Strip HTML tags for preview
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <div className="space-y-6">
      <Card className="border border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Textbausteine</CardTitle>
            <CardDescription>
              Verwalte Textbausteine (A-Z), die Teilnehmern angezeigt werden
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {textbausteine.length > 0 && (
              <Button
                onClick={() => setShowDeleteAllDialog(true)}
                variant="outline"
                size="sm"
                className="text-red-600 hover:bg-red-50 border-red-200"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Alle löschen
              </Button>
            )}
            {/* Export Button */}
            {textbausteine.length > 0 && (
              <Button
                onClick={handleExport}
                variant="outline"
                size="sm"
              >
                <Download className="h-4 w-4 mr-1" />
                Export
              </Button>
            )}
            {/* Import Button */}
            <Button
              onClick={() => importFileRef.current?.click()}
              variant="outline"
              size="sm"
            >
              <Upload className="h-4 w-4 mr-1" />
              Import
            </Button>
            <input
              type="file"
              ref={importFileRef}
              onChange={handleImport}
              accept=".json"
              className="hidden"
            />
            <Button
              onClick={handleCreate}
              size="sm"
              data-testid="create-textbaustein-button"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={textbausteine.length >= 26}
            >
              <Plus className="h-4 w-4 mr-1" />
              Neu erstellen
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
            </div>
          ) : textbausteine.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Keine Textbausteine vorhanden</p>
              <p className="text-sm mt-1">Erstelle deinen ersten Textbaustein oder importiere eine JSON-Datei</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {textbausteine.map(tb => (
                <Card 
                  key={tb.kennung} 
                  data-testid={`textbaustein-${tb.kennung}`} 
                  className="border hover:shadow-md transition-shadow group"
                  style={{
                    borderColor: tb.has_border ? '#3b82f6' : undefined,
                    borderWidth: tb.has_border ? '2px' : undefined,
                    backgroundColor: tb.background_color || undefined
                  }}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-blue-600 text-white text-base font-bold px-3 py-1">
                          {tb.kennung}
                        </Badge>
                        <button
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('admin_token');
                              const response = await axios.patch(
                                `${API}/admin/textbausteine/${tb.kennung}/toggle-active`,
                                {},
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              toast.success(response.data.active ? 'Textbaustein aktiviert' : 'Textbaustein deaktiviert');
                              fetchTextbausteine();
                            } catch (error) {
                              toast.error('Fehler beim Aktualisieren');
                            }
                          }}
                          className="shrink-0 hover:scale-110 transition-transform"
                          title={tb.active !== false ? 'Textbaustein deaktivieren' : 'Textbaustein aktivieren'}
                        >
                          <div className={`h-3 w-3 rounded-full ${tb.active !== false ? 'bg-green-500' : 'bg-red-500'}`} />
                        </button>
                        {tb.has_border && (
                          <Square className="h-3.5 w-3.5 text-blue-500" title="Mit Rahmen" />
                        )}
                        {tb.show_in_pruefung && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium" title="Auch im Prüfungsmodus sichtbar">
                            Prüfung
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(tb)}
                          data-testid={`edit-textbaustein-${tb.kennung}`}
                          className="h-8 w-8 p-0 text-gray-600 hover:text-blue-600"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog open={deleteTarget === tb.kennung} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteTarget(tb.kennung)}
                            data-testid={`delete-textbaustein-${tb.kennung}`}
                            className="h-8 w-8 p-0 text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Textbaustein löschen?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Möchtest du den Textbaustein &quot;{tb.kennung}{tb.titel ? ` - ${tb.titel}` : ''}&quot; wirklich löschen?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={handleDelete}
                                className="bg-red-600 hover:bg-red-700 text-white"
                              >
                                Löschen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                    {tb.titel && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('admin_token');
                              const formData = new FormData();
                              formData.append('kennung', tb.kennung);
                              formData.append('titel', tb.titel || '');
                              formData.append('emoji', tb.emoji || '');
                              formData.append('show_title', tb.show_title !== false ? 'false' : 'true');
                              formData.append('inhalt', tb.inhalt);
                              formData.append('code', '');
                              formData.append('has_border', tb.has_border ? 'true' : 'false');
                              formData.append('has_line_above', tb.has_line_above ? 'true' : 'false');
                              formData.append('has_line_below', tb.has_line_below ? 'true' : 'false');
                              formData.append('background_color', tb.background_color || '');
                              
                              await axios.put(
                                `${API}/admin/textbausteine/${tb.kennung}`,
                                formData,
                                { 
                                  headers: { 
                                    Authorization: `Bearer ${token}`,
                                    'Content-Type': 'multipart/form-data'
                                  } 
                                }
                              );
                              toast.success(tb.show_title !== false ? 'Überschrift versteckt' : 'Überschrift sichtbar');
                              fetchTextbausteine();
                            } catch (error) {
                              toast.error('Fehler beim Aktualisieren');
                            }
                          }}
                          className="shrink-0 hover:scale-110 transition-transform"
                          title={tb.show_title !== false ? 'Überschrift verstecken' : 'Überschrift anzeigen'}
                        >
                          {tb.show_title !== false ? (
                            <Eye className="h-4 w-4 text-green-600" />
                          ) : (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                        <CardTitle className="text-base font-semibold text-gray-900 line-clamp-1 flex-1 flex items-center gap-1">
                          {tb.emoji && <span>{tb.emoji}</span>}
                          {tb.titel}
                        </CardTitle>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="pt-0 space-y-3">
                    <p className="text-sm text-gray-600 line-clamp-3">
                      {stripHtml(tb.inhalt)}
                    </p>
                    
                    {tb.file_name && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-2">
                        <Paperclip className="h-4 w-4 text-blue-600 shrink-0" />
                        <span className="truncate font-medium">{tb.file_name}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">{editingBaustein ? 'Textbaustein bearbeiten' : 'Neuen Textbaustein erstellen'}</DialogTitle>
            <DialogDescription>
              {editingBaustein 
                ? `Bearbeite Textbaustein "${formData.kennung}"${formData.titel ? ` - ${formData.titel}` : ''}`
                : 'Wähle eine Kennung (A-Z) und fülle die Felder aus.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 2-Spalten: Kennung + Überschrift */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">Kennung</Label>
                {editingBaustein ? (
                  <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                    <div className="text-4xl font-bold text-center text-blue-600">{formData.kennung}</div>
                    <Select 
                      value={formData.kennung} 
                      onValueChange={(value) => setFormData({ ...formData, kennung: value })}
                    >
                      <SelectTrigger className="mt-2 h-10 text-sm">
                        <SelectValue placeholder="Ändern..." />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableLetters(editingBaustein?.kennung).map(letter => (
                          <SelectItem key={letter} value={letter} className="text-lg py-2">
                            {letter} {letter === editingBaustein?.kennung && '(aktuell)'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <Select 
                    value={formData.kennung} 
                    onValueChange={(value) => setFormData({ ...formData, kennung: value })}
                  >
                    <SelectTrigger id="kennung" data-testid="textbaustein-kennung-select" className="h-16 text-3xl font-bold">
                      <SelectValue placeholder="?" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableLetters().map(letter => (
                        <SelectItem key={letter} value={letter} className="text-2xl py-3">
                          {letter}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">
                  Überschrift <span className="text-xs font-normal text-gray-500">(optional)</span>
                </Label>
                <div className="flex gap-2 items-center">
                  {/* Emoji Picker */}
                  <div className="relative flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="h-12 w-12 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 text-2xl"
                      title="Emoji auswählen"
                      data-testid="emoji-picker-button"
                    >
                      {formData.emoji || <span className="text-gray-400 text-sm">+</span>}
                    </button>
                    {/* X-Button zum Entfernen des Emojis */}
                    {formData.emoji && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, emoji: '' })}
                        className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                        title="Emoji entfernen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    {showEmojiPicker && (
                      <div className="absolute top-full left-0 mt-1 p-3 bg-white rounded-lg shadow-lg border border-gray-200 z-50 w-64">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500 font-medium">Emoji auswählen</span>
                          {formData.emoji && (
                            <button
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, emoji: '' });
                                setShowEmojiPicker(false);
                              }}
                              className="text-xs text-red-500 hover:text-red-700"
                            >
                              Entfernen
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-8 gap-1">
                          {['📌', '⚠️', '✅', '❌', '📝', '📅', '🎵', '🎹', '🎸', '🎺', '🎻', '🥁', '🎤', '🎧', '📱', '📚', '📖', '✏️', '📍', '⭐', '💡', '🔔', '📢', '💬', '❗', '❓', '🏆', '🎯', '🎓', '📋', '📎', '🔗', '💰'].map((emoji) => (
                            <button
                              key={emoji}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, emoji });
                                setShowEmojiPicker(false);
                              }}
                              className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded text-lg"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <Input
                    id="titel"
                    value={formData.titel || ''}
                    onChange={(e) => setFormData({ ...formData, titel: e.target.value })}
                    data-testid="textbaustein-titel-input"
                    placeholder="z.B. Wichtiger Hinweis"
                    className="text-base h-12 flex-1"
                  />
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <Checkbox
                    id="show_title"
                    checked={formData.show_title}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_title: checked })}
                    className="data-[state=checked]:bg-blue-600 h-5 w-5"
                  />
                  <Label htmlFor="show_title" className="text-sm font-medium cursor-pointer flex items-center gap-2 flex-1">
                    {formData.show_title ? <Eye className="h-4 w-4 text-blue-600" /> : <EyeOff className="h-4 w-4 text-gray-400" />}
                    <span>Überschrift im Dashboard anzeigen</span>
                  </Label>
                </div>
              </div>
            </div>

            <Separator />

            {/* Rich Text Editor - TipTap */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-700">Inhalt</Label>
              <TipTapEditor
                content={formData.inhalt}
                onChange={(html) => setFormData({ ...formData, inhalt: html })}
                placeholder="Inhalt eingeben..."
              />
              <p className="text-xs text-gray-500">
                Text markieren und dann Formatierung wählen. Tastenkürzel: Strg+B (Fett), Strg+I (Kursiv), Strg+U (Unterstrichen)
              </p>
            </div>

            <Separator />

            {/* Darstellungsoptionen */}
            <div className="space-y-4">
              <Label className="text-sm font-medium text-gray-700">Darstellung im Dashboard</Label>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Rahmen */}
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <Checkbox
                    id="has_border"
                    checked={formData.has_border}
                    onCheckedChange={(checked) => setFormData({ ...formData, has_border: checked })}
                    className="data-[state=checked]:bg-blue-600 h-5 w-5"
                  />
                  <Label htmlFor="has_border" className="text-sm font-medium cursor-pointer flex-1">
                    Mit Rahmen anzeigen
                  </Label>
                </div>
                
                {/* Im Prüfungsmodus anzeigen */}
                <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <Checkbox
                    id="show_in_pruefung"
                    checked={formData.show_in_pruefung}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_in_pruefung: checked })}
                    className="data-[state=checked]:bg-purple-600 h-5 w-5"
                  />
                  <Label htmlFor="show_in_pruefung" className="text-sm font-medium cursor-pointer flex-1">
                    Auch im Prüfungsmodus anzeigen
                  </Label>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <Label className="text-sm font-medium mb-3 block">Hintergrundfarbe</Label>
                  <div className="flex flex-wrap gap-2">
                    {BACKGROUND_COLORS.map((color) => (
                      <button
                        key={color.name}
                        type="button"
                        onClick={() => setFormData({ ...formData, background_color: color.value })}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          formData.background_color === color.value 
                            ? 'border-blue-600 scale-110 ring-2 ring-blue-200' 
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        style={{ backgroundColor: color.value || '#ffffff' }}
                        title={color.name}
                      >
                        {!color.value && (
                          <span className="text-gray-400 text-xs">∅</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Trennlinien */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <Checkbox
                    id="has_line_above"
                    checked={formData.has_line_above}
                    onCheckedChange={(checked) => setFormData({ ...formData, has_line_above: checked })}
                    className="data-[state=checked]:bg-blue-600 h-5 w-5"
                    data-testid="checkbox-line-above"
                  />
                  <Label htmlFor="has_line_above" className="text-sm font-medium cursor-pointer flex-1">
                    Linie oben
                  </Label>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <Checkbox
                    id="has_line_below"
                    checked={formData.has_line_below}
                    onCheckedChange={(checked) => setFormData({ ...formData, has_line_below: checked })}
                    className="data-[state=checked]:bg-blue-600 h-5 w-5"
                    data-testid="checkbox-line-below"
                  />
                  <Label htmlFor="has_line_below" className="text-sm font-medium cursor-pointer flex-1">
                    Linie unten
                  </Label>
                </div>
              </div>
            </div>

            <Separator />

            {/* Dateianhang */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-3 block">
                Dateianhang <span className="text-xs font-normal text-gray-500">(optional - max. 10 MB)</span>
              </Label>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {editingBaustein?.file_name && !removeFile && !selectedFile && (
                  <div className="p-4 rounded-lg border-2 border-green-200 bg-green-50">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Paperclip className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-green-900 truncate">{editingBaustein.file_name}</p>
                          <p className="text-xs text-green-700">Aktuell hochgeladen</p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setRemoveFile(true)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-100 flex-shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
                
                <div className={editingBaustein?.file_name && !removeFile && !selectedFile ? "md:col-span-1" : "md:col-span-2"}>
                  <label 
                    htmlFor="file" 
                    className="block p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 transition-colors cursor-pointer bg-gray-50 hover:bg-blue-50"
                  >
                    <div className="text-center">
                      <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="mt-2 text-sm font-medium text-gray-700">
                        {selectedFile ? selectedFile.name : 'Datei auswählen'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">PDF, Bilder, Dokumente</p>
                    </div>
                  </label>
                  <Input
                    id="file"
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      setSelectedFile(e.target.files[0]);
                      setRemoveFile(false);
                    }}
                    data-testid="textbaustein-file-input"
                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="textbaustein-save-button"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete All Confirmation */}
      <AlertDialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alle Textbausteine löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Es werden alle {textbausteine.length} Textbausteine und deren Dateien unwiderruflich gelöscht. Tippe &quot;LÖSCHEN&quot; zur Bestätigung.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={deleteAllConfirm}
            onChange={(e) => setDeleteAllConfirm(e.target.value)}
            placeholder='Tippe "LÖSCHEN"'
            className="my-2"
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteAllConfirm('')}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAll}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteAllConfirm !== 'LÖSCHEN'}
            >
              Alle löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TextbausteineTab;
